<template>
  <div v-if="noModels" class="text-caption text-medium-emphasis" style="max-width: 240px; line-height: 1.3">
    No models enabled — please reach out to your org owner.
  </div>
  <v-select
    v-else
    :rules="[rules.required]"
    :modelValue="displayValue"
    :items="aiModels"
    item-title="name"
    item-value="version"
    label="Select a model"
    placeholder="Select a model"
    hide-no-data
    hide-details
    density="compact"
    variant="outlined"
    style="min-width: 190px"
    @update:model-value="saveAiVersion"
    :disabled="$store.state.isWorkflowDisabled"
    :menu-props="{ maxHeight: 400, location: 'bottom' }"
  />
</template>

<script>
import workflowApi from '@/api/workflowApi'
import { mapState } from 'vuex'
import { effectiveAiVersions } from '@/shared/utils/effectiveAiVersions'
import { AI_MODELS } from '@/constants/aiModels'

export default {
  name: 'AiVersionSelector',
  props: {
    modelValue: {
      type: Number,
      required: true
    }
  },
  data () {
    return {
      allAiModels: AI_MODELS,
      rules: {
        required: value => !!value || 'Required.'
      }
    }
  },
  computed: {
    ...mapState({
      workflow: state => state.workflow.workflow,
      organisations: state => state.organisations.organisations,
      projectMemberships: state => state.projectMemberships.projectMemberships
    }),
    // Resolve the org for the workflow currently open. Prefer the org-native
    // activeProject (resolved via the gated getProject for the route projectId);
    // fall back to the legacy membership list. The legacy list is null for org
    // users until a create/update populates it, which previously left currentOrg
    // null and silently failed effectiveAiVersions OPEN to the full model catalog
    // (bypassing the org's AI-model allowlist).
    currentOrg () {
      const projectId = this.$route.params.projectId
      if (!projectId || !this.organisations) {
        return null
      }
      const activeProject = this.$store.state.projectMemberships?.activeProject
      let organisationId = activeProject?.id === projectId ? activeProject?.organisationId : null
      if (!organisationId && this.projectMemberships) {
        organisationId = this.projectMemberships.find((m) => m.project?.id === projectId)?.project?.organisationId
      }
      if (!organisationId) {
        return null
      }
      return this.organisations.find((o) => o.id === organisationId) || null
    },
    catalog () {
      return this.allAiModels.map((m) => m.version)
    },
    allowedVersions () {
      return effectiveAiVersions(this.catalog, this.currentOrg)
    },
    aiModels () {
      return this.allAiModels.filter((m) => this.allowedVersions.includes(m.version))
    },
    // No models available for this org (owner enabled nothing valid and no default).
    noModels () {
      return this.aiModels.length === 0
    },
    // Only show a value when it's an available model — otherwise the placeholder,
    // never a raw version number.
    displayValue () {
      return this.aiModels.some((m) => m.version === this.modelValue) ? this.modelValue : null
    }
  },
  methods: {
    async saveAiVersion (newVal) {
      this.$emit('update:modelValue', newVal)
      await workflowApi.updateWorkflowNoHistoryMutation(
        this.$route.params.projectId,
        {
          id: this.$route.params.id,
          expectedVersion: this.workflow.version,
          aiVersion: newVal
        }
      )
    }
  },
  watch: {
    workflow: {
      handler (newVal) {
        // Resolve the model to show: the saved one if still available, else the
        // org default if available, else the first available model, else none.
        const allowed = this.allowedVersions
        const saved = newVal.aiVersion
        const orgDefault = this.currentOrg?.defaultAiVersion
        let resolved = null
        if (saved && allowed.includes(saved)) {
          resolved = saved
        } else if (orgDefault && allowed.includes(orgDefault)) {
          resolved = orgDefault
        } else if (allowed.length) {
          resolved = allowed[0]
        }
        this.$emit('update:modelValue', resolved)
      },
      deep: true,
      immediate: true
    }
  }
}

</script>
